'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Mail, ArrowLeft, RotateCw } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { authClient } from '@/lib/auth/client';
import {
  signupSchema,
  type SignupFormValues,
} from '@/features/auth/types/auth-schemas';
import { SocialLoginButtons } from './social-login-buttons';
import { PasswordStrengthIndicator } from './password-strength-indicator';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// ── Check email view ────────────────────────────────────────────

function CheckEmailView({ email }: { email: string }) {
  const t = useTranslations('auth.signup.checkEmail');
  const [cooldown, setCooldown] = useState(60);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function handleResend() {
    try {
      setResending(true);
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: '/role-select',
      });
      if (result.error) {
        toast.error(t('resendFailed'));
        return;
      }
      toast.success(t('resendSuccess'));
      setCooldown(60);
    } catch {
      toast.error(t('resendFailed'));
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="space-y-6 animate-scale-in">
      {/* Icon */}
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
          <Mail className="size-8 text-primary" />
        </div>
      </div>

      {/* Heading */}
      <div className="space-y-2 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('description')}{' '}
          <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>

      {/* Resend */}
      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          className="w-full h-11 gap-2"
          onClick={handleResend}
          disabled={cooldown > 0 || resending}
        >
          {resending ? (
            <Spinner className="size-4" />
          ) : (
            <RotateCw className="size-4" />
          )}
          {cooldown > 0
            ? t('resendCooldown', { seconds: cooldown })
            : t('resendButton')}
        </Button>

        <p className="text-center text-xs text-muted-foreground">
          {t('spamNote')}
        </p>
      </div>

      {/* Back link */}
      <div className="text-center">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          {t('backToLogin')}
        </Link>
      </div>
    </div>
  );
}

// ── Main signup form ────────────────────────────────────────────

export function SignupForm() {
  const t = useTranslations('auth.signup');
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
    },
  });

  const password = form.watch('password');
  const isSubmitting = form.formState.isSubmitting;

  async function onSubmit(values: SignupFormValues) {
    setEmailError(null);
    try {
      const result = await authClient.signUp.email({
        name: values.name,
        email: values.email,
        password: values.password,
        callbackURL: '/role-select',
      });

      if (result.error) {
        const msg = result.error.message ?? '';
        if (
          msg.toLowerCase().includes('already') ||
          msg.toLowerCase().includes('exist') ||
          msg.toLowerCase().includes('email')
        ) {
          setEmailError(t('emailAlreadyRegistered'));
        } else {
          toast.error(msg || t('connectionError'));
        }
        return;
      }

      setSubmittedEmail(values.email);
      setSubmitted(true);
    } catch {
      toast.error(t('connectionError'));
    }
  }

  if (submitted) {
    return <CheckEmailView email={submittedEmail} />;
  }

  return (
    <div className="space-y-6 animate-scale-in">
      {/* Header */}
      <div className="space-y-1.5">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {t('title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
      </div>

      {/* Social login */}
      <SocialLoginButtons callbackURL="/role-select" />

      {/* Divider */}
      <div className="flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs text-muted-foreground shrink-0">
          {t('orContinueWith')}
        </span>
        <Separator className="flex-1" />
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('nameLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('namePlaceholder')}
                    autoComplete="name"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('emailLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    autoComplete="email"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                {emailError && (
                  <p className="text-sm text-destructive">
                    {emailError}{' '}
                    <Link
                      href="/login"
                      className="underline underline-offset-2 hover:text-destructive/80 transition-colors"
                    >
                      {t('logInInstead')}
                    </Link>
                  </p>
                )}
              </FormItem>
            )}
          />

          {/* Password + strength indicator */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('passwordLabel')}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t('passwordPlaceholder')}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                    {...field}
                  />
                </FormControl>
                <PasswordStrengthIndicator password={password} />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit */}
          <Button
            type="submit"
            className="w-full h-11 gap-2 font-semibold mt-2 transition-transform hover:-translate-y-0.5 duration-200"
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="size-4" />}
            {isSubmitting ? t('loading') : t('submitButton')}
          </Button>
        </form>
      </Form>

      {/* Footer */}
      <p className="text-center text-sm text-muted-foreground">
        {t('haveAccount')}{' '}
        <Link
          href="/login"
          className="font-medium text-primary hover:text-primary/80 transition-colors"
        >
          {t('signIn')}
        </Link>
      </p>
    </div>
  );
}
